/**
 * useFounderAdmin — typed wrapper around the founder-admin-action and
 * analytics-export edge functions. Every mutation is server-audited.
 */
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type AdminKind =
  | "set_tier"
  | "suspend_subscription"
  | "resume_subscription"
  | "cancel_subscription"
  | "mark_test_account"
  | "unmark_test_account"
  | "block_user"
  | "unblock_user"
  | "soft_delete_user"
  | "restore_user"
  | "hard_delete_user"
  | "assign_role"
  | "revoke_role"
  | "warn_user";

export interface AdminCallArgs {
  kind: AdminKind;
  target_user_id?: string;
  payload?: Record<string, unknown>;
  reason?: string;
  password?: string;
}

export const useFounderAdmin = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (args: AdminCallArgs) => {
    setBusy(true);
    setError(null);
    const { data, error: err } = await supabase.functions.invoke<{ ok: boolean; error?: string }>(
      "founder-admin-action",
      { body: args },
    );
    setBusy(false);
    if (err) {
      setError(err.message);
      return { ok: false, error: err.message };
    }
    if (data && !data.ok && data.error) {
      setError(data.error);
      return { ok: false, error: data.error };
    }
    return { ok: true };
  }, []);

  return { run, busy, error };
};

export type ExportFormat = "json" | "csv" | "xlsx" | "pdf" | "zip";
export type ExportCluster = "a" | "b" | "c" | "d";

export interface ExportArgs {
  clusters?: ExportCluster[];
  from?: string;
  to?: string;
  format: ExportFormat;
  packet_type?: string;
}

export const useAnalyticsExport = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (args: ExportArgs) => {
    setBusy(true);
    setError(null);
    try {
      // For binary formats, hit the function URL directly so we receive a Blob.
      if (args.format === "csv" || args.format === "pdf") {
        const sess = await supabase.auth.getSession();
        const token = sess.data.session?.access_token;
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
        const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
        const res = await fetch(`${SUPABASE_URL}/functions/v1/analytics-export`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token ?? ""}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(args),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `HTTP ${res.status}`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `wavlynq-investor-packet.${args.format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return { ok: true };
      }
      // JSON / xlsx-stub / zip-stub return through the SDK
      const { data, error: err } = await supabase.functions.invoke("analytics-export", {
        body: args,
      });
      if (err) throw err;
      return { ok: true, data };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "export_failed";
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, []);

  return { run, busy, error };
};

/** Read a cluster MV directly for in-portal dashboards (founder-only RLS). */
export const useCluster = () => {
  const fetchCluster = useCallback(
    async (cluster: ExportCluster, sinceDays = 30) => {
      const since = new Date(Date.now() - sinceDays * 86400_000).toISOString();
      const view = {
        a: "mv_cluster_a_tech",
        b: "mv_cluster_b_economics",
        c: "mv_cluster_c_community",
        d: "mv_cluster_d_safety",
      }[cluster];
      const { data, error } = await supabase
        .from(view as never)
        .select("*")
        .gte("day", since)
        .order("day", { ascending: false })
        .limit(200);
      if (error) return { rows: [] as Record<string, unknown>[], error: error.message };
      return { rows: (data as Record<string, unknown>[]) ?? [] };
    },
    [],
  );
  return { fetchCluster };
};
